/**
 * Import/Export Component - Manage rig configurations
 * 
 * Allows users to:
 * - Export current rig to JSON file
 * - Import rig from JSON file
 * - Clear current rig
 */

import React, { useRef } from 'react';
import { Button, DropdownMenu, Dialog, Flex, Text, TextArea, Box } from '@radix-ui/themes';
import { DownloadIcon, UploadIcon, TrashIcon, Share1Icon } from '@radix-ui/react-icons';
import { useDevices } from '../../contexts/DeviceContext';
import { Device } from '../../types';

const ImportExport: React.FC = () => {
  const { devices, importRig, exportRig, clearRig } = useDevices();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showImportDialog, setShowImportDialog] = React.useState(false);
  const [importJson, setImportJson] = React.useState('');

  const handleExportToFile = () => {
    const data = exportRig();
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `astropower-rig-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImportFromFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = e.target?.result as string;
        const data = JSON.parse(json) as Device[];
        
        // Validate it's an array
        if (!Array.isArray(data)) {
          alert('❌ Invalid file format: Expected an array of devices');
          return;
        }

        if (window.confirm(`Import rig with ${data.length} device(s)? This will replace your current rig.`)) {
          importRig(data);
          alert(`✅ Imported ${data.length} device(s) successfully!`);
        }
      } catch (error) {
        console.error('Import error:', error);
        alert('❌ Failed to import file. Please check the file format.');
      }
    };
    reader.readAsText(file);

    // Reset input so same file can be selected again
    event.target.value = '';
  };

  const handleImportFromJson = () => {
    try {
      const data = JSON.parse(importJson) as Device[];
      
      if (!Array.isArray(data)) {
        alert('❌ Invalid format: Expected an array of devices');
        return;
      }

      if (window.confirm(`Import rig with ${data.length} device(s)? This will replace your current rig.`)) {
        importRig(data);
        setShowImportDialog(false);
        setImportJson('');
        alert(`✅ Imported ${data.length} device(s) successfully!`);
      }
    } catch (error) {
      console.error('Import error:', error);
      alert('❌ Invalid JSON format. Please check your input.');
    }
  };

  const handleCopyToClipboard = () => {
    const data = exportRig();
    const json = JSON.stringify(data, null, 2);
    navigator.clipboard.writeText(json).then(() => {
      alert('✅ Rig configuration copied to clipboard!');
    }).catch(() => {
      alert('❌ Failed to copy to clipboard');
    });
  };

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <Button variant="soft">
            <Share1Icon /> Import/Export
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content>
          <DropdownMenu.Item onClick={handleExportToFile}>
            <DownloadIcon /> Export to File
          </DropdownMenu.Item>
          <DropdownMenu.Item onClick={handleCopyToClipboard}>
            <Share1Icon /> Copy to Clipboard
          </DropdownMenu.Item>
          <DropdownMenu.Separator />
          <DropdownMenu.Item onClick={handleImportFromFile}>
            <UploadIcon /> Import from File
          </DropdownMenu.Item>
          <DropdownMenu.Item onClick={() => setShowImportDialog(true)}>
            <UploadIcon /> Import from JSON
          </DropdownMenu.Item>
          <DropdownMenu.Separator />
          <DropdownMenu.Item color="red" onClick={clearRig}>
            <TrashIcon /> Clear Rig
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Root>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Import from JSON Dialog */}
      <Dialog.Root open={showImportDialog} onOpenChange={setShowImportDialog}>
        <Dialog.Content style={{ maxWidth: 600 }}>
          <Dialog.Title>Import Rig from JSON</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            Paste your rig configuration JSON below
          </Dialog.Description>

          <Box mb="4">
            <TextArea
              value={importJson}
              onChange={(e) => setImportJson(e.target.value)}
              placeholder='[{"id": "...", "name": "...", ...}]'
              rows={15}
              style={{ fontFamily: 'monospace', fontSize: '12px' }}
            />
          </Box>

          <Flex gap="3" justify="between" align="center">
            <Text size="1" color="gray">
              {devices.length} device(s) currently in rig
            </Text>
            <Flex gap="2">
              <Dialog.Close>
                <Button variant="soft" color="gray">Cancel</Button>
              </Dialog.Close>
              <Button onClick={handleImportFromJson} disabled={!importJson.trim()}>
                Import
              </Button>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
};

export default ImportExport;
